import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Box, Chip, Typography, useTheme } from "@mui/material";

/**
 * Formats an ISO date string into a human-readable form, e.g. "March 9, 2026".
 * Falls back to the raw string if parsing fails.
 */
const formatDate = (isoString) => {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return isoString;
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};

/**
 * Extracts the language identifier from a fenced code block's className.
 * react-markdown sets className="language-js" (or similar) on <code> elements
 * that come from fenced blocks.
 */
const parseLanguage = (className) => {
  const match = /language-(\w+)/.exec(className ?? "");
  return match ? match[1] : null;
};

/**
 * Renders a fenced code block using react-syntax-highlighter.
 * This component is mapped to the `pre` element so it receives the full
 * <pre><code className="language-…">…</code></pre> subtree from react-markdown,
 * which is the idiomatic v10 pattern for distinguishing block vs inline code.
 */
const CodeBlock = ({ children }) => {
  // react-markdown v10 nests a single <code> child inside every <pre>.
  const codeChild = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === "code"
  );

  if (!codeChild) {
    // Fallback: render as a plain pre if the structure is unexpected.
    return <pre>{children}</pre>;
  }

  const language = parseLanguage(codeChild.props.className);
  const code = String(codeChild.props.children).replace(/\n$/, "");

  return (
    <SyntaxHighlighter
      language={language ?? "text"}
      style={vscDarkPlus}
      // Prevent a nested <pre> from causing horizontal scroll on narrow viewports
      customStyle={{
        margin: "1.25rem 0",
        borderRadius: "6px",
        fontSize: "0.875rem",
        // maxWidth: 100% keeps the block from overflowing its container
        maxWidth: "100%",
        overflowX: "auto",
      }}
      wrapLongLines={false}
      PreTag="div"
    >
      {code}
    </SyntaxHighlighter>
  );
};

/**
 * Full blog post renderer. Accepts a `post` object and renders:
 * - Title, published date, and tags (mirroring PostCard conventions)
 * - The full Markdown body with styled prose, headings, code blocks, and links
 *
 * All colors and fonts are sourced from the MUI theme via `useTheme()` so this
 * component adapts correctly when nested under blogTheme or vsDarkTheme.
 */
const PostDetail = ({ post }) => {
  const theme = useTheme();
  const publishedDate = formatDate(post.published_at);

  /**
   * Maps react-markdown element names to MUI-styled renderers.
   * The `node` prop is destructured and discarded to prevent it from being
   * forwarded to DOM elements (which would trigger a React warning).
   */
  const mdComponents = {
    // ── Block-level prose ────────────────────────────────────────────────────

    // Paragraphs use body1, which blogTheme overrides to proportional sans-serif
    // with lineHeight 1.75 — no explicit fontFamily needed here.
    // eslint-disable-next-line no-unused-vars
    p: ({ node, ...props }) => (
      <Typography
        variant="body1"
        component="p"
        sx={{ mb: 2, color: "text.primary" }}
        {...props}
      />
    ),

    // Markdown headings delegate to blogTheme's typography variants so that
    // h1–h4 pick up Fira Code and the VS Code token colors automatically.
    // eslint-disable-next-line no-unused-vars
    h1: ({ node, ...props }) => (
      <Typography variant="h1" component="h1" sx={{ mt: 4, mb: 1.5 }} {...props} />
    ),
    // eslint-disable-next-line no-unused-vars
    h2: ({ node, ...props }) => (
      <Typography variant="h2" component="h2" sx={{ mt: 3.5, mb: 1 }} {...props} />
    ),
    // eslint-disable-next-line no-unused-vars
    h3: ({ node, ...props }) => (
      <Typography variant="h3" component="h3" sx={{ mt: 3, mb: 1 }} {...props} />
    ),
    // eslint-disable-next-line no-unused-vars
    h4: ({ node, ...props }) => (
      <Typography variant="h4" component="h4" sx={{ mt: 2.5, mb: 0.75 }} {...props} />
    ),
    // h5/h6 fall outside blogTheme's explicit overrides; use body1 weight for
    // visual hierarchy without introducing new font families.
    // eslint-disable-next-line no-unused-vars
    h5: ({ node, ...props }) => (
      <Typography
        variant="body1"
        component="h5"
        sx={{
          mt: 2,
          mb: 0.5,
          fontWeight: 700,
          fontFamily: "'Fira Code', monospace",
          color: theme.palette.text.secondary,
        }}
        {...props}
      />
    ),
    // eslint-disable-next-line no-unused-vars
    h6: ({ node, ...props }) => (
      <Typography
        variant="body1"
        component="h6"
        sx={{
          mt: 1.5,
          mb: 0.5,
          fontWeight: 700,
          fontFamily: "'Fira Code', monospace",
          color: theme.palette.text.secondary,
        }}
        {...props}
      />
    ),

    // Blockquotes adopt the comment color (green) — a deliberate VS Code metaphor
    // where quoted text reads like an editorial annotation.
    // eslint-disable-next-line no-unused-vars
    blockquote: ({ node, ...props }) => (
      <Box
        component="blockquote"
        sx={{
          borderLeft: `3px solid ${theme.palette.comment.main}`,
          pl: 2,
          ml: 0,
          my: 2,
          color: theme.palette.comment.main,
          fontStyle: "italic",
        }}
        {...props}
      />
    ),

    // ── Lists ────────────────────────────────────────────────────────────────

    // eslint-disable-next-line no-unused-vars
    ul: ({ node, ...props }) => (
      <Box
        component="ul"
        sx={{
          pl: 3,
          mb: 2,
          "& li": { mb: 0.5 },
        }}
        {...props}
      />
    ),
    // eslint-disable-next-line no-unused-vars
    ol: ({ node, ...props }) => (
      <Box
        component="ol"
        sx={{
          pl: 3,
          mb: 2,
          "& li": { mb: 0.5 },
        }}
        {...props}
      />
    ),
    // eslint-disable-next-line no-unused-vars
    li: ({ node, ...props }) => (
      <Typography
        component="li"
        variant="body1"
        sx={{ color: "text.primary" }}
        {...props}
      />
    ),

    // ── Code ─────────────────────────────────────────────────────────────────

    // Block code: react-markdown v10 wraps fenced blocks in <pre><code>.
    // Mapping `pre` intercepts the entire structure before it hits the DOM
    // so SyntaxHighlighter can receive the raw code string and language.
    pre: CodeBlock,

    // Inline code: bare <code> elements that are NOT inside a <pre>.
    // These have no language className, so we style them as monospace pills.
    // eslint-disable-next-line no-unused-vars
    code: ({ node, ...props }) => (
      <Box
        component="code"
        sx={{
          fontFamily: "'Fira Code', monospace",
          fontSize: "0.875em",
          backgroundColor: "background.paper",
          color: theme.palette.string.main,
          borderRadius: "4px",
          px: 0.6,
          py: 0.2,
          // Prevent long inline tokens from causing horizontal overflow
          wordBreak: "break-word",
        }}
        {...props}
      />
    ),

    // ── Links ────────────────────────────────────────────────────────────────

    // eslint-disable-next-line no-unused-vars
    a: ({ node, ...props }) => (
      <Box
        component="a"
        sx={{
          color: theme.palette.primary.main,
          textDecorationColor: theme.palette.primary.main,
          "&:hover": {
            color: theme.palette.text.secondary,
            textDecorationColor: theme.palette.text.secondary,
          },
        }}
        {...props}
      />
    ),

    // ── Horizontal rule ──────────────────────────────────────────────────────

    // eslint-disable-next-line no-unused-vars
    hr: ({ node, ...props }) => (
      <Box
        component="hr"
        sx={{
          border: "none",
          borderTop: `1px solid ${theme.palette.divider}`,
          my: 3,
        }}
        {...props}
      />
    ),
  };

  return (
    <Box
      component="article"
      sx={{
        // maxWidth keeps long lines readable; overflow-x hidden prevents any
        // wide code block from blowing out the layout on narrow viewports.
        maxWidth: "72ch",
        mx: "auto",
        overflowX: "hidden",
      }}
    >
      {/* ── Post header ───────────────────────────────────────────────────── */}

      {/* Title — blogTheme h1 styles this in Fira Code + primary.main blue */}
      <Typography
        variant="h1"
        component="h1"
        sx={{ mb: 0.75, lineHeight: 1.2 }}
      >
        {post.title}
      </Typography>

      {/* Published date — mirrors the "// comment" aesthetic from PostCard */}
      {publishedDate && (
        <Typography
          variant="caption"
          component="time"
          dateTime={post.published_at}
          sx={{
            display: "block",
            color: theme.palette.comment.main,
            fontFamily: "'Fira Code', monospace",
            mb: 1.5,
          }}
        >
          {`// ${publishedDate}`}
        </Typography>
      )}

      {/* Tags — identical chip styling to PostCard */}
      {Array.isArray(post.tags) && post.tags.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 3 }}>
          {post.tags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              variant="outlined"
              sx={{
                color: theme.palette.keyword.main,
                borderColor: theme.palette.keyword.main,
                backgroundColor: "transparent",
                fontFamily: "'Fira Code', monospace",
                fontSize: "0.7rem",
              }}
            />
          ))}
        </Box>
      )}

      {/* ── Post body ─────────────────────────────────────────────────────── */}

      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
        {post.body}
      </ReactMarkdown>
    </Box>
  );
};

export default PostDetail;
